// AuthedLayout — the shell frame for authenticated routes: skip-link + the pinned
// top (ShellTop: the project strips over the Navbar banner) + the single <main>
// landmark wrapping the Outlet. ProtectedRoute
// wraps the Outlet so the auth gate fires inside the layout (Navbar renders
// during the /me-fetch loading state — cleaner than a bare spinner).
//
// The <main id="main-content"> lives here (not per-frame) so the skip-link has
// a target on EVERY authed page — framed (Project/Admin) and project-less
// (profile, select-project, …) alike. The frames (ProjectLayout / AdminLayout)
// render their content + chrome inside this main; the admin section nav is a
// <nav> sibling of the page content within main.

import { useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from './Navbar.js';
import { ShellTop } from './ShellTop.js';
import { ProjectStrips } from './ProjectStrips.js';
import { ProtectedRoute } from '../auth/ProtectedRoute.js';
import { useOptionalProjectParams } from '../../hooks/useProjectParams.js';
import { useSyncLastProject } from '../../hooks/useSyncLastProject.js';
import { useFocusMainOnNavigate } from '../../hooks/useFocusMainOnNavigate.js';

export function AuthedLayout() {
  const { t } = useTranslation('nav');
  // Keep cached /me.last_project_id in step with the URL project (covers every
  // /p/:projectId route — domain + admin frames alike) so the `/` resolver
  // lands on the true last-visited project. Local cache write, no API call.
  useSyncLastProject(useOptionalProjectParams());
  // SPA route-change focus → move focus to <main> on navigation (a11y).
  const mainRef = useRef<HTMLElement>(null);
  useFocusMainOnNavigate(mainRef);
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-canvas)] text-[var(--color-text-primary)]">
      {/* Skip-to-content — first focusable; an in-page fragment link (not router
          navigation), the canonical a11y pattern. Visible only when focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-tooltip focus:px-3 focus:py-2 focus:rounded-md focus:bg-[var(--color-bg-page)] focus:text-[var(--color-text-primary)] focus:border focus:border-[var(--color-border)] focus:shadow-overlay focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-primary-ring)]"
      >
        {t('skipToContent')}
      </a>
      {/* The frame's pinned top — the project strips over the bar, ONE block that
          measures itself (lib/shellTop): nothing below hard-codes its height. */}
      <ShellTop strips={<ProjectStrips />}>
        <Navbar />
      </ShellTop>
      <ProtectedRoute>
        <main ref={mainRef} id="main-content" tabIndex={-1} className="flex-1 flex flex-col min-w-0 outline-none">
          <Outlet />
        </main>
      </ProtectedRoute>
    </div>
  );
}
